import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { makeT } from "@/lib/i18n";

const AppContext = createContext(null);

const CART_KEY = "tk_cart_v2";
const EXTRAS_KEY = "tk_extras_v2";
const PKG_KEY = "tk_package_v2";
const LANG_KEY = "tk_lang";
const EVENT_KEY = "tk_event_v1";

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [extras, setExtras] = useState(() => {
    try {
      const raw = localStorage.getItem(EXTRAS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [selectedPackageId, setSelectedPackageId] = useState(() => localStorage.getItem(PKG_KEY) || null);
  const [eventInfo, setEventInfo] = useState(() => {
    try {
      const raw = localStorage.getItem(EVENT_KEY);
      return raw ? JSON.parse(raw) : { type: "", guests: "" };
    } catch { return { type: "", guests: "" }; }
  });
  const [customerToken, setCustomerToken] = useState(() => localStorage.getItem("tk_customer_token") || null);
  const [customerInfo, setCustomerInfo] = useState(() => {
    try {
      const raw = localStorage.getItem("tk_customer_info");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("tk_admin_token") || null);
  const [adminInfo, setAdminInfo] = useState(() => {
    try {
      const raw = localStorage.getItem("tk_admin_info");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const t = useMemo(() => makeT(lang), [lang]);

  useEffect(() => { localStorage.setItem(LANG_KEY, lang); }, [lang]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(EXTRAS_KEY, JSON.stringify(extras)); }, [extras]);
  useEffect(() => {
    if (selectedPackageId) localStorage.setItem(PKG_KEY, selectedPackageId);
    else localStorage.removeItem(PKG_KEY);
  }, [selectedPackageId]);
  useEffect(() => { localStorage.setItem(EVENT_KEY, JSON.stringify(eventInfo)); }, [eventInfo]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) { console.warn("Settings load failed", e?.message); }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Dish selection (package items)
  const addToCart = useCallback((dish) => {
    setCart((prev) => {
      if (prev.find((c) => c.id === dish.id)) return prev; // package dishes are singleton
      return [...prev, {
        id: dish.id, name: dish.name, image_id: dish.image_id || null,
        category_id: dish.category_id, pricing_unit: dish.pricing_unit || null, qty: 1,
      }];
    });
  }, []);
  const removeFromCart = useCallback((dishId) => {
    setCart((prev) => prev.filter((c) => c.id !== dishId));
  }, []);
  const decrementCart = removeFromCart;
  const setQty = useCallback(() => {}, []);
  const clearCart = useCallback(() => {
    setCart([]); setExtras([]); setSelectedPackageId(null);
    setEventInfo({ type: "", guests: "" });
  }, []);

  // Extras selection — separate list
  const addExtra = useCallback((dish, category) => {
    setExtras((prev) => {
      const existing = prev.find((e) => e.dish_id === dish.id);
      if (existing) return prev.map((e) => e.dish_id === dish.id ? { ...e, qty: e.qty + 1 } : e);
      return [...prev, {
        dish_id: dish.id, dish_name: dish.name, category_id: category.id,
        category_name: category.name, image_id: dish.image_id || null, qty: 1,
      }];
    });
  }, []);
  const decrementExtra = useCallback((dishId) => {
    setExtras((prev) => prev.map((e) => e.dish_id === dishId ? { ...e, qty: e.qty - 1 } : e).filter((e) => e.qty > 0));
  }, []);
  const removeExtra = useCallback((dishId) => {
    setExtras((prev) => prev.filter((e) => e.dish_id !== dishId));
  }, []);

  const setCustomerAuth = useCallback((token, info) => {
    if (token) localStorage.setItem("tk_customer_token", token); else localStorage.removeItem("tk_customer_token");
    if (info) localStorage.setItem("tk_customer_info", JSON.stringify(info)); else localStorage.removeItem("tk_customer_info");
    setCustomerToken(token); setCustomerInfo(info);
  }, []);
  const logoutCustomer = useCallback(() => {
    setCustomerAuth(null, null);
    clearCart();
  }, [setCustomerAuth, clearCart]);

  const setAdminAuth = useCallback((token, info) => {
    if (token) localStorage.setItem("tk_admin_token", token); else localStorage.removeItem("tk_admin_token");
    if (info) localStorage.setItem("tk_admin_info", JSON.stringify(info)); else localStorage.removeItem("tk_admin_info");
    setAdminToken(token); setAdminInfo(info);
  }, []);
  const logoutAdmin = useCallback(() => setAdminAuth(null, null), [setAdminAuth]);

  const value = {
    lang, setLang, t, settings, reloadSettings: loadSettings,
    cart, cartCount: cart.length + extras.reduce((n, e) => n + e.qty, 0),
    addToCart, removeFromCart, decrementCart, setQty, clearCart,
    extras, addExtra, decrementExtra, removeExtra,
    selectedPackageId, setSelectedPackageId,
    eventInfo, setEventInfo,
    adminToken, adminInfo, setAdminAuth, logoutAdmin,
    customerToken, customerInfo, setCustomerAuth, logoutCustomer,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
